// Shared time validation and calculation utilities for time entry components

// Helper function to safely parse dates
export const safeParseDate = (dateValue: string | Date | undefined): Date => {
  if (!dateValue) return new Date();
  
  // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    const [year, month, day] = dateValue.split('-').map(Number);
    return new Date(year, month - 1, day); // Create date in local timezone
  }
  
  const parsed = new Date(dateValue);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
};

// Helper function to format time with proper rounding
export const formatTimeString = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Helper function to convert 24-hour time to 12-hour format
export const formatTimeTo12Hour = (time24: string): string => {
  if (!time24) return "";
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Helper function to calculate hours from time strings
export const calculateHoursFromTime = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 0;
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;
  
  const diffMinutes = endTotalMinutes - startTotalMinutes;
  return Math.max(0, diffMinutes / 60);
};

// Helper function to calculate end time from start time and hours
export const calculateEndTimeFromHours = (startTime: string, hours: number): string => {
  if (!startTime || hours <= 0) return startTime;
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const totalMinutes = startHour * 60 + startMinute + Math.round(hours * 60);
  
  const endHour = Math.floor(totalMinutes / 60) % 24;
  const endMinute = totalMinutes % 60;
  
  return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
};

// Format duration to a clean decimal string
export const formatDuration = (duration: number | null | undefined): string => {
  if (!duration) return "";
  // Round to 2 decimal places to avoid floating point precision issues
  return (Math.round(duration * 100) / 100).toString();
};

// Helper function to validate time range
export const validateTimeRange = (startTime: string, endTime: string): { isValid: boolean; error?: string } => {
  if (!startTime || !endTime) {
    return { isValid: true }; // Allow partial input
  }

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;
  
  if (endTotalMinutes <= startTotalMinutes) {
    return { 
      isValid: false, 
      error: "End time must be after start time" 
    };
  }
  
  return { isValid: true };
};

// Helper function to calculate maximum hours based on start time
export const getMaxHours = (startTime: string): number => {
  if (!startTime) return 24; // If no start time, max 24 hours
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute;
  const endOfDayMinutes = 24 * 60; // 24:00 (midnight next day)
  
  const remainingMinutes = endOfDayMinutes - startTotalMinutes;
  return remainingMinutes / 60;
};

// Helper function to get minimum end time based on start time
export const getMinEndTime = (startTime: string): string => {
  if (!startTime) return "00:00";
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute + 1; // At least 1 minute after start
  
  const endHour = Math.floor(startTotalMinutes / 60) % 24;
  const endMinute = startTotalMinutes % 60;
  
  return `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
};

// Helper function to validate and clamp hours
export const validateAndClampHours = (hours: number, startTime: string): number => {
  const maxHours = getMaxHours(startTime);
  return Math.min(Math.max(0, hours), maxHours);
};

// Validate time entry data before saving
export const validateTimeEntryData = (data: {
  startTime?: string;
  endTime?: string;
  hours?: string;
}): { isValid: boolean; error?: string } => {
  const hasHours = data.hours && parseFloat(data.hours) > 0;
  const hasTimeRange = data.startTime && data.endTime;

  if (!hasHours && !hasTimeRange) {
    return {
      isValid: false,
      error: "Please enter either hours or both start and end times"
    };
  }

  if (hasTimeRange) {
    const validation = validateTimeRange(data.startTime!, data.endTime!);
    if (!validation.isValid) {
      return validation;
    }
  }

  // Validate hours don't exceed day limit
  if (hasHours && data.startTime) {
    const hoursNum = parseFloat(data.hours!);
    const maxHours = getMaxHours(data.startTime);
    if (hoursNum > maxHours) {
      return {
        isValid: false,
        error: `Hours cannot exceed ${formatDuration(maxHours)} from this start time`
      };
    }
  }

  return { isValid: true };
};

// Sync time fields - calculate hours from time range
export const syncTimeToHours = (startTime: string, endTime: string): string => {
  if (!startTime || !endTime) return "";
  
  const validation = validateTimeRange(startTime, endTime);
  if (!validation.isValid) return "";
  
  const calculatedHours = calculateHoursFromTime(startTime, endTime);
  return calculatedHours > 0 ? formatDuration(calculatedHours) : "";
};

// Sync hours to end time - calculate end time from start time and hours
export const syncHoursToEndTime = (startTime: string, hours: string): { endTime: string; hours: string } => {
  if (!startTime || !hours) return { endTime: "", hours };
  
  const hoursNum = parseFloat(hours);
  if (isNaN(hoursNum) || hoursNum <= 0) return { endTime: "", hours };
  
  // Validate and clamp hours
  const validatedHours = validateAndClampHours(hoursNum, startTime);
  const newEndTime = calculateEndTimeFromHours(startTime, validatedHours);
  
  return {
    endTime: newEndTime,
    hours: validatedHours !== hoursNum ? formatDuration(validatedHours) : hours
  };
};

// Create time entry payload with proper validation
export const createTimeEntryPayload = (data: {
  date: Date;
  startTime?: string;
  endTime?: string;
  hours?: string;
  description?: string;
  billable?: boolean;
  status?: string;
  moduleType?: string;
  moduleId?: string;
}) => {
  const hasTimeRange = data.startTime && data.endTime;

  if (hasTimeRange) {
    // Timer entry - use start and end times
    const [startHour, startMinute] = data.startTime!.split(":").map(Number);
    const [endHour, endMinute] = data.endTime!.split(":").map(Number);
    
    // IMPORTANT: Both startTime and endTime use the date from entryDate
    const startTime = new Date(data.date);
    startTime.setHours(startHour, startMinute, 0, 0);
    
    const endTime = new Date(data.date);
    endTime.setHours(endHour, endMinute, 0, 0);

    // Calculate duration for timer entries
    const duration = calculateHoursFromTime(data.startTime!, data.endTime!);

    return {
      entryType: 'TIMER' as const,
      entryDate: data.date,
      startTime: startTime,
      endTime: endTime,
      duration: duration,
      description: data.description,
      billable: data.billable,
      status: data.status,
      moduleType: data.moduleType === "NONE" ? undefined : data.moduleType,
      moduleId: data.moduleType === "NONE" ? undefined : data.moduleId,
    };
  } else {
    // Manual entry - duration only
    const hours = parseFloat(data.hours!);
    return {
      entryType: 'MANUAL' as const,
      entryDate: data.date,
      duration: hours,
      description: data.description,
      billable: data.billable,
      status: data.status,
      moduleType: data.moduleType === "NONE" ? undefined : data.moduleType,
      moduleId: data.moduleType === "NONE" ? undefined : data.moduleId,
    };
  }
}; 